import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect, useCallback, useState } from 'react';
import { PatientEncounter, InfoCell } from '@codametrix/shared-views';
import { PatientEncounterDashboardColumns, codeBenchActions, commonEnums, AmplifyCore, cmxDateTime } from '@codametrix/ui-common';
import { useSelector, useDispatch } from 'react-redux';
import { createPortal } from 'react-dom';
import { styles } from './patient-timeline-styles';
import { eventTypes, filteredEventTypes, resolveBodyTemplate, resolveHeadTemplate, resolveFootTemplate, isInteractionFromCase, Message, MessageHeader, MessageBody, MessageFooter } from './patient-timeline-entries';
import { MenuButton, MenuItem } from '@codametrix/shared-views';
import { ButtonToggle } from '../../components/button-toggle/button-toggle';
import { LoadingSlugs, Conditional } from '@codametrix/ui-components';
import { CheckboxMenu } from '../../components/checkbox-menu/checkbox-menu';
import './patient-timeline-styles.scss';
import { HStack, Flex, Box } from '@chakra-ui/react';
import { BiomeIcon, IconName } from '@codametrix/verdigris';
import { resolveTimelineTimeZone } from '../../utils/timezone';
const { conditionalUtils } = AmplifyCore;
const { DateFilterLabelEnum } = commonEnums;
const classes = styles();
const fieldEditorInfo = {
    caseEdits: {
        caseEdits: {}
    },
    updateValue: (val)=>console.log(val),
    updateCase: (val)=>console.log(val)
};
const filteredTypes = [
    'ADT'
];
const fallbackElement = document.createElement('div');
const isEntryIntoDateRange = (event_datetime, days)=>{
    const currentDate = new Date();
    const parsedDate = new Date(cmxDateTime.clearSquareBraces(event_datetime));
    const timeDifferenceInDays = Math.floor((currentDate.getTime() - parsedDate.getTime()) / (24 * 60 * 60 * 1000));
    return timeDifferenceInDays <= days;
};
const PatientTimeline = ()=>{
    const [showAll, setShowAll] = useState(false);
    const dispatch = useDispatch();
    const { activeCaseRun, isOpen, entries, entriesLoading, entriesMessageCountsLoading, entriesMessageCounts, entriesSumCounts, entriesLastPage, filterTypes, entriesFailed, dateFilter, entryPage, dateFilterStr, isFirstLoad, orderTypeStatus } = useSelector((state)=>{
        return state.patientTimeline;
    });
    const { capabilities } = useSelector((state)=>{
        return state.capabilities;
    });
    const ui = useSelector((state)=>state.ui);
    const organizationTimeZone = useSelector((state)=>state.organization?.activeOrganization?.timeZoneFullName);
    const preferredTimeZone = resolveTimelineTimeZone({
        caseRunTimeZone: activeCaseRun?.tenant_timezone,
        activeCaseRunTimeZone: activeCaseRun?.tenant_timezone,
        organizationTimeZone
    });
    const isTimelineInfiniteLoadOn = conditionalUtils.capabilityValue(capabilities?.timelineInfiniteLoad?.on);
    const filterOps = isTimelineInfiniteLoadOn ? {
        dateStr: dateFilterStr,
        messageTypes: filterTypes
    } : {};
    // get message counts for mrn
    useEffect(()=>{
        if (isOpen) {
            if (isTimelineInfiniteLoadOn) {
                dispatch(codeBenchActions.getPatientMessageCountsWithFilter({
                    caseRun: activeCaseRun,
                    useDataAccessAmplify: capabilities?.useDataAccessAmplify?.on,
                    ui
                }));
            } else {
                dispatch(codeBenchActions.getPatientMessageCounts({
                    caseRun: activeCaseRun,
                    useDataAccessAmplify: capabilities?.useDataAccessAmplify?.on,
                    ui
                }));
            }
        }
    // eslint-disable-next-line
    }, [
        activeCaseRun,
        isOpen
    ]);
    const dispatchCaseEntries = useCallback(async (caseEntriesProp)=>{
        const { newPage, localEntriesSumCounts, dateStr, endDateStr, messageTypes } = caseEntriesProp;
        const pageCount = !isTimelineInfiniteLoadOn && !!localEntriesSumCounts ? localEntriesSumCounts : 200;
        const filterOps = isTimelineInfiniteLoadOn ? {
            messageTypeFilter: messageTypes ?? filterTypes ?? [],
            dateFilter: dateStr ?? dateFilterStr ?? '',
            endDateFilter: endDateStr ?? `${dateFilterStr} 23:59:59`
        } : {};
        if (!entriesLoading) {
            try {
                try {
                    if (isTimelineInfiniteLoadOn) {
                        await dispatch(codeBenchActions.getCaseEntriesWithFilter({
                            caseRun: activeCaseRun,
                            limit: pageCount,
                            page: newPage,
                            timelineInfiniteLoad: capabilities?.timelineInfiniteLoad?.on,
                            organizationTimeZone,
                            useDataAccessAmplify: capabilities?.useDataAccessAmplify?.on,
                            ui,
                            ...filterOps
                        }));
                    } else {
                        await dispatch(codeBenchActions.getCaseEntries({
                            caseRun: activeCaseRun,
                            limit: pageCount,
                            page: newPage,
                            timelineInfiniteLoad: capabilities?.timelineInfiniteLoad?.on,
                            organizationTimeZone,
                            useDataAccessAmplify: capabilities?.useDataAccessAmplify?.on,
                            ui
                        }));
                    }
                } catch (err) {
                    throw err;
                }
                setShouldDispatch(true);
                if (newPage !== 1) {
                    const cmxDrawerBody = document.getElementById('cmx-drawer-body');
                    const drawerBody = cmxDrawerBody?.closest('.chakra-modal__body');
                    if (drawerBody) {
                        drawerBody.scrollTop = drawerBody?.scrollHeight - drawerBody.clientHeight - 20;
                    }
                }
            } catch (err) {
                throw err;
            }
        }
    }, // eslint-disable-next-line
    [
        activeCaseRun,
        dispatch
    ]);
    // fetch for the very first time
    useEffect(()=>{
        if (entriesSumCounts !== 0 && activeCaseRun?.patient_identifier?.number && isOpen && isFirstLoad) {
            setShowAll(false);
            dispatchCaseEntries({
                newPage: entryPage,
                localEntriesSumCounts: entriesSumCounts,
                ...filterOps
            });
        }
    // eslint-disable-next-line
    }, [
        isOpen,
        entriesSumCounts,
        activeCaseRun?.patient_identifier?.number
    ]);
    const [shouldDispatch, setShouldDispatch] = useState(true);
    // request every time scroll touches the final position
    useEffect(()=>{
        const cmxDrawerBody = document.getElementById('cmx-drawer-body');
        const drawerBody = cmxDrawerBody?.closest('.chakra-modal__body');
        const handleScroll = ()=>{
            if (drawerBody) {
                const distanceToBottom = drawerBody.scrollHeight - (drawerBody.scrollTop + drawerBody.clientHeight);
                if (drawerBody && shouldDispatch) {
                    if (distanceToBottom <= 5 && !entriesLastPage) {
                        setShouldDispatch(false);
                        drawerBody?.removeEventListener('scroll', handleScroll);
                        dispatchCaseEntries({
                            newPage: entryPage + 1,
                            localEntriesSumCounts: entriesSumCounts,
                            ...filterOps
                        });
                    }
                }
            }
        };
        if (drawerBody && isOpen && conditionalUtils.capabilityValue(capabilities?.timelineInfiniteLoad?.on)) {
            drawerBody.addEventListener('scroll', handleScroll);
        }
        if (entriesLastPage) {
            drawerBody?.removeEventListener('scroll', handleScroll);
        }
        return ()=>{
            // Clean up the event listener when the component unmounts
            if (drawerBody) {
                drawerBody.removeEventListener('scroll', handleScroll);
            }
        };
    // eslint-disable-next-line
    }, [
        isOpen,
        shouldDispatch,
        entryPage,
        entriesLastPage,
        dispatchCaseEntries
    ]);
    let filteredEntries = entries?.filter((entry)=>Object.keys(eventTypes).includes(entry?.message?.type));
    const lengthNoSupportedInter = filteredEntries?.length;
    const hasFilterTypes = filterTypes?.length > 0;
    if (!showAll && !hasFilterTypes && !isTimelineInfiniteLoadOn) {
        filteredEntries = entries?.filter((entry)=>!(filteredTypes.includes(entry?.message?.type) && filteredEventTypes[entry?.message?.type] && !filteredEventTypes[entry?.message?.type].includes(entry?.message?.code)));
    }
    const lengthNoShownInter = filteredEntries?.length;
    const setFilterTypes = (filterTypes)=>{
        if (isTimelineInfiniteLoadOn) {
            dispatch(codeBenchActions.setFilterTypesInfinite(filterTypes));
            dispatchCaseEntries({
                newPage: 1,
                localEntriesSumCounts: entriesSumCounts,
                dateStr: dateFilterStr,
                messageTypes: filterTypes
            });
        } else {
            dispatch(codeBenchActions.setFilterTypes(filterTypes));
        }
    };
    const setFilterDates = (filterDate)=>{
        let startDate = '', endDate = '';
        let dateFilterState = {
            dateFilter: filterDate
        };
        let requestBody = {
            caseRun: activeCaseRun,
            useDataAccessAmplify: capabilities?.useDataAccessAmplify?.on,
            ui
        };
        const specialFilters = [
            DateFilterLabelEnum.ON_DOS,
            DateFilterLabelEnum.ALL_EVENTS
        ];
        if (!specialFilters.includes(filterDate)) {
            startDate = getDateForPastPeriod(filterDate)?.toString();
            requestBody = {
                ...requestBody,
                dateFilter: startDate
            };
            dateFilterState = {
                ...dateFilterState,
                dateFilterStr: startDate
            };
        }
        if (filterDate === DateFilterLabelEnum.ON_DOS && activeCaseRun?.date_of_service) {
            const dateOfService = activeCaseRun?.date_of_service;
            startDate = cmxDateTime.format(dateOfService, cmxDateTime.FORMATS.DATE);
            endDate = `${startDate} 23:59:59`;
            requestBody = {
                ...requestBody,
                dateFilter: startDate,
                endDateFilter: endDate
            };
            dateFilterState = {
                ...dateFilterState,
                dateFilterStr: startDate,
                endDateFilterStr: endDate
            };
        }
        if (isTimelineInfiniteLoadOn) {
            dispatch(codeBenchActions.setFilterDatesInfinite(dateFilterState));
            dispatch(codeBenchActions.getPatientMessageCountsWithFilter({
                ...requestBody,
                useDataAccessAmplify: capabilities?.useDataAccessAmplify?.on,
                ui
            }));
            dispatchCaseEntries({
                newPage: 1,
                localEntriesSumCounts: entriesSumCounts,
                dateStr: startDate,
                endDateStr: endDate,
                messageTypes: filterTypes
            });
        } else {
            dispatch(codeBenchActions.setFilterDates(dateFilterState));
            dispatch(codeBenchActions.getPatientMessageCounts({
                ...requestBody,
                useDataAccessAmplify: capabilities?.useDataAccessAmplify?.on,
                ui
            }));
        }
    };
    const getDateForPastPeriod = (dateFilter)=>{
        let days = 0;
        switch(dateFilter){
            case commonEnums.DateFilterLabelEnum.SEVEN_DAYS:
                days = 7;
                break;
            case commonEnums.DateFilterLabelEnum.THIRTY_DAYS:
                days = 30;
                break;
            case commonEnums.DateFilterLabelEnum.THREE_YEARS:
                days = 3 * 365;
                break;
        }
        const today = new Date();
        const pastDate = new Date(today);
        pastDate.setDate(today.getDate() - days);
        return cmxDateTime.format(pastDate.toISOString(), cmxDateTime.FORMATS.DATE);
    };
    // filter by types
    if (hasFilterTypes) {
        filteredEntries = filteredEntries.filter((entry)=>filterTypes.map((fType)=>fType.value).includes(entry?.message?.type));
    }
    const isEntryIntoDateRangeFilter = (rangeParams)=>{
        const { entry, numberDays, onDateStr } = rangeParams;
        const eventTime = entry.message.type === 'MDM' ? entry?.note_date : entry.message.type === 'SIU' ? entry?.service_date : entry?.meta?.event_datetime;
        if (!!numberDays) {
            return isEntryIntoDateRange(eventTime, numberDays);
        } else {
            const parsedDate = new Date(cmxDateTime.clearSquareBraces(eventTime));
            const eventDateStr = cmxDateTime.format(parsedDate, cmxDateTime.FORMATS.DATE);
            return eventDateStr === onDateStr;
        }
    };
    // filter by date
    if (!isTimelineInfiniteLoadOn) {
        switch(dateFilter){
            case commonEnums.DateFilterLabelEnum.SEVEN_DAYS:
                filteredEntries = filteredEntries.filter((entry)=>isEntryIntoDateRangeFilter({
                        entry,
                        numberDays: 7
                    }));
                break;
            case commonEnums.DateFilterLabelEnum.THIRTY_DAYS:
                filteredEntries = filteredEntries.filter((entry)=>isEntryIntoDateRangeFilter({
                        entry,
                        numberDays: 30
                    }));
                break;
            case commonEnums.DateFilterLabelEnum.THREE_YEARS:
                filteredEntries = filteredEntries.filter((entry)=>isEntryIntoDateRangeFilter({
                        entry,
                        numberDays: 365 * 3
                    }));
                break;
            case commonEnums.DateFilterLabelEnum.ON_DOS:
                if (!!activeCaseRun?.date_of_service) {
                    filteredEntries = filteredEntries.filter((entry)=>isEntryIntoDateRangeFilter({
                            entry,
                            onDateStr: activeCaseRun?.date_of_service?.toString()
                        }));
                }
                break;
        }
    }
    const entriesMessageType = Object.keys(commonEnums.EntriesMessageType).sort((entryTypeA, entryTypeB)=>{
        if (commonEnums.EntriesMessageType[entryTypeB] < commonEnums.EntriesMessageType[entryTypeA]) {
            return 1;
        } else {
            return -1;
        }
    });
    return /*#__PURE__*/ _jsx(Conditional, {
        on: isOpen,
        children: (entriesLoading || entriesMessageCountsLoading) && entryPage === 1 && shouldDispatch ? /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ createPortal(/*#__PURE__*/ _jsx(_Fragment, {
                    children: /*#__PURE__*/ _jsx(LoadingSlugs, {
                        width: "25",
                        numberItems: 1
                    }, "loading-slugs")
                }), document.getElementById('cmx-drawer-header') || fallbackElement),
                /*#__PURE__*/ createPortal(/*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx(LoadingSlugs, {
                            width: "25",
                            numberItems: 1
                        }, "loading-slugs"),
                        /*#__PURE__*/ _jsx(LoadingSlugs, {
                            width: "80",
                            numberItems: 1
                        }, "loading-slugs"),
                        /*#__PURE__*/ _jsx(LoadingSlugs, {
                            numberItems: 3
                        }, "loading-slugs"),
                        /*#__PURE__*/ _jsx("br", {}),
                        /*#__PURE__*/ _jsx(LoadingSlugs, {
                            width: "60",
                            numberItems: 5
                        }, "loading-slugs"),
                        /*#__PURE__*/ _jsx("br", {}),
                        /*#__PURE__*/ _jsx(LoadingSlugs, {
                            width: "60",
                            numberItems: 5
                        }, "loading-slugs"),
                        /*#__PURE__*/ _jsx("br", {}),
                        /*#__PURE__*/ _jsx(LoadingSlugs, {
                            width: "60",
                            numberItems: 5
                        }, "loading-slugs")
                    ]
                }), document.getElementById('cmx-drawer-body') || fallbackElement)
            ]
        }) : /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(Conditional, {
                    on: !!document.getElementById('cmx-drawer-header'),
                    children: /*#__PURE__*/ _jsx(_Fragment, {
                        children: /*#__PURE__*/ createPortal(/*#__PURE__*/ _jsx("div", {
                            className: "more-patient-title",
                            "data-testid": "more-patient-title",
                            children: "More Patient Data"
                        }), document.getElementById('cmx-drawer-header') || fallbackElement)
                    })
                }),
                /*#__PURE__*/ _jsx(Conditional, {
                    on: !!document.getElementById('cmx-drawer-body'),
                    children: /*#__PURE__*/ _jsx(_Fragment, {
                        children: /*#__PURE__*/ createPortal(/*#__PURE__*/ _jsxs(Box, {
                            className: "patient-timeline-body",
                            __css: classes.timelineRoot,
                            children: [
                                /*#__PURE__*/ _jsxs(Box, {
                                    __css: classes.timelineHeader,
                                    children: [
                                        /*#__PURE__*/ _jsx(Box, {
                                            __css: classes.patientBlock,
                                            children: /*#__PURE__*/ _jsx(PatientEncounter, {
                                                propertyConfig: PatientEncounterDashboardColumns.patientTimeline,
                                                fieldEditorInfo: fieldEditorInfo,
                                                activeItem: activeCaseRun
                                            })
                                        }),
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "patient-timeline-case-comparison",
                                            style: classes.caseComparison,
                                            children: [
                                                /*#__PURE__*/ _jsx("h4", {
                                                    children: "Compare with current case"
                                                }),
                                                /*#__PURE__*/ _jsxs("div", {
                                                    className: "patient-timeline-case-comparison-fields",
                                                    children: [
                                                        /*#__PURE__*/ _jsx(InfoCell, {
                                                            className: "patient-timeline-compare-info-cell",
                                                            label: "date of service",
                                                            value: activeCaseRun.date_of_service?.toString() ?? '',
                                                            cellKey: "drawer-date-of-service"
                                                        }),
                                                        /*#__PURE__*/ _jsx(InfoCell, {
                                                            className: "patient-timeline-compare-info-cell",
                                                            label: "specialty",
                                                            value: activeCaseRun.specialty?.toString() ?? '',
                                                            cellKey: "drawer-specialty"
                                                        })
                                                    ]
                                                })
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsx(Flex, {
                                            style: classes.filtersBox,
                                            children: /*#__PURE__*/ _jsxs(HStack, {
                                                style: classes.filtersSection,
                                                className: `filters-section ${!!filterTypes.length ? 'filter-type-active' : ''}`,
                                                children: [
                                                    /*#__PURE__*/ _jsxs(_Fragment, {
                                                        children: [
                                                            /*#__PURE__*/ _jsx("div", {
                                                                style: classes.filterType,
                                                                children: /*#__PURE__*/ _jsx(CheckboxMenu, {
                                                                    options: entriesMessageType.map((key)=>({
                                                                            label: `${commonEnums.EntriesMessageType[key]} (${entriesMessageCounts?.length > 0 ? entriesMessageCounts.find((entryMessageCount)=>entryMessageCount.message_type === key)?.count ?? 0 : 'N/A'})`,
                                                                            value: key
                                                                        })),
                                                                    label: "Select Type",
                                                                    onApplyFilters: setFilterTypes,
                                                                    selectedOptions: filterTypes,
                                                                    menuRightIcon: /*#__PURE__*/ _jsx(BiomeIcon, {
                                                                        iconName: IconName.Chevron_Down,
                                                                        iconSize: "14",
                                                                        titleText: "Open",
                                                                        iconClass: `${classes.downChevron}`
                                                                    })
                                                                })
                                                            }),
                                                            /*#__PURE__*/ _jsx("div", {
                                                                style: classes.filterDate,
                                                                children: /*#__PURE__*/ _jsxs(MenuButton, {
                                                                    label: dateFilter,
                                                                    className: `${dateFilter !== commonEnums.DateFilterLabelEnum.ALL_EVENTS ? 'filter-active' : ''}`,
                                                                    children: [
                                                                        /*#__PURE__*/ _jsx(MenuItem, {
                                                                            onSelect: ()=>setFilterDates(commonEnums.DateFilterLabelEnum.ALL_EVENTS),
                                                                            children: "All Events"
                                                                        }),
                                                                        /*#__PURE__*/ _jsx(MenuItem, {
                                                                            onSelect: ()=>setFilterDates(commonEnums.DateFilterLabelEnum.SEVEN_DAYS),
                                                                            children: "Past 7 days"
                                                                        }),
                                                                        /*#__PURE__*/ _jsx(MenuItem, {
                                                                            onSelect: ()=>setFilterDates(commonEnums.DateFilterLabelEnum.THIRTY_DAYS),
                                                                            children: "Past 30 days"
                                                                        }),
                                                                        /*#__PURE__*/ _jsx(MenuItem, {
                                                                            onSelect: ()=>setFilterDates(commonEnums.DateFilterLabelEnum.THREE_YEARS),
                                                                            children: "Past 3 years"
                                                                        }),
                                                                        /*#__PURE__*/ _jsx(MenuItem, {
                                                                            onSelect: ()=>setFilterDates(commonEnums.DateFilterLabelEnum.ON_DOS),
                                                                            children: commonEnums.DateFilterLabelEnum.ON_DOS
                                                                        })
                                                                    ]
                                                                })
                                                            })
                                                        ]
                                                    }),
                                                    /*#__PURE__*/ _jsx("div", {
                                                        style: classes.filterShowAll,
                                                        children: (showAll && filterTypes?.length === 0 || filteredEntries?.length > 0 && lengthNoSupportedInter !== lengthNoShownInter) && /*#__PURE__*/ _jsx(ButtonToggle, {
                                                            style: classes.buttonToggle,
                                                            label: showAll ? 'Showing All' : 'Not Showing All',
                                                            active: showAll,
                                                            onSelect: ()=>setShowAll(!showAll)
                                                        })
                                                    }),
                                                    /*#__PURE__*/ _jsxs("div", {
                                                        className: "tenantTimezoneSection",
                                                        children: [
                                                            /*#__PURE__*/ _jsx("span", {
                                                                className: "field-label",
                                                                children: "All times listed in"
                                                            }),
                                                            /*#__PURE__*/ _jsx("span", {
                                                                className: "field-data",
                                                                children: preferredTimeZone?.replace(/_/g, ' ') ?? ''
                                                            })
                                                        ]
                                                    })
                                                ]
                                            })
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs(Box, {
                                    __css: classes.entriesScrollRegion,
                                    children: [
                                        /*#__PURE__*/ _jsx("div", {
                                            style: classes.entriesSection,
                                            className: "entries-section",
                                            "data-testid": "entries-section",
                                            children: filteredEntries?.length > 0 ? filteredEntries.map((entry, entryIndex)=>{
                                                return /*#__PURE__*/ _jsxs(Message, {
                                                    type: entry?.message?.type,
                                                    match: isInteractionFromCase(entry, activeCaseRun),
                                                    size: "md",
                                                    "data-entry-type": entry?.message?.type,
                                                    "data-entry-code": entry?.message?.code,
                                                    children: [
                                                        /*#__PURE__*/ _jsx(MessageHeader, {
                                                            children: resolveHeadTemplate(activeCaseRun, entry, orderTypeStatus)
                                                        }),
                                                        /*#__PURE__*/ _jsx(MessageBody, {
                                                            children: resolveBodyTemplate(activeCaseRun, entry)
                                                        }),
                                                        /*#__PURE__*/ _jsx(MessageFooter, {
                                                            children: resolveFootTemplate(activeCaseRun, entry)
                                                        })
                                                    ]
                                                }, `entry-${entryIndex}`);
                                            }) : /*#__PURE__*/ _jsx(_Fragment, {
                                                children: !entriesFailed && !entriesLoading && /*#__PURE__*/ _jsx("div", {
                                                    className: "empty-message",
                                                    style: classes.notFoundMessage,
                                                    children: "Events for this patient have not been found."
                                                })
                                            })
                                        }),
                                        entriesLoading && /*#__PURE__*/ _jsx(LoadingSlugs, {
                                            width: "100",
                                            numberItems: 3
                                        }, "loading-slugs"),
                                        entriesFailed && /*#__PURE__*/ _jsx("div", {
                                            className: "empty-message",
                                            style: classes.notFoundMessageError,
                                            children: "Error loading entries, try again later."
                                        })
                                    ]
                                })
                            ]
                        }), document.getElementById('cmx-drawer-body') || fallbackElement)
                    })
                })
            ]
        })
    });
};
PatientTimeline.displayName = 'PatientTimeline';
export { PatientTimeline };
